import { syncWorkspaceLatestMessageChanges } from '../../lib/workspaceLatestMessages';

import type { ChannelId } from '../core/types';
import { EchoDexieBuffer } from './dexieBuffer';

let bufferInstance: EchoDexieBuffer | null = null;
let activeChannelId: ChannelId | null = null;
const healthListeners = new Set<(healthy: boolean) => void>();

/** Fans a durable-storage health change out to registered listeners. */
export function notifyFeedBufferHealth(healthy: boolean): void {
  for (const listener of healthListeners) listener(healthy);
}

/**
 * Returns the page-lifetime feed buffer, constructing it on first use. Lazy construction
 * keeps the Dexie database (named from current-user meta at import time) off module load.
 */
export function getFeedBuffer(): EchoDexieBuffer {
  bufferInstance ??= new EchoDexieBuffer({
    onHealthChange: notifyFeedBufferHealth,
    onMessageRowsChanged: syncWorkspaceLatestMessageChanges,
  });
  return bufferInstance;
}

/** Marks a channel as owned by a mounted feed session so the replicator skips it; returns the release. */
export function claimActiveFeedChannel(channelId: ChannelId): () => void {
  activeChannelId = channelId;
  return () => {
    // Only clear this claim: on a keyed remount the next surface may have already
    // claimed its channel before the old release runs.
    if (activeChannelId === channelId) {
      activeChannelId = null;
    }
  };
}

/** Returns the channel currently owned by a mounted feed session, if any. */
export function getActiveFeedChannelId(): ChannelId | null {
  return activeChannelId;
}

/** Registers a buffer health listener; returns the unsubscribe. */
export function registerBufferHealthListener(listener: (healthy: boolean) => void): () => void {
  healthListeners.add(listener);
  return () => {
    healthListeners.delete(listener);
  };
}

/** Test-only: drops the buffer instance, active claim, and health listeners. */
export function resetFeedCacheForTests(): void {
  bufferInstance = null;
  activeChannelId = null;
  healthListeners.clear();
}
